import { useEffect, useState } from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import { useFormik } from 'formik';

import { PersonalDataFormSkeleton } from '../skeletons/PersonalDataFormSkeleton';

import MevoInput from 'src/components/Input';
import Typography from 'src/components/Typography';
import { Box } from 'src/components/UI/Box';
import { Button } from 'src/components/UI/Button';
import { Flex } from 'src/components/UI/Flex';
import { MASKS } from 'src/components/UI/Input/MaskedInput';
import { FormControl } from 'src/components/UI/FormControl';

import { Yup } from 'src/helpers/Yup';
import { validateCPF } from 'src/helpers/cpf';
import { onlyDigits } from 'src/helpers/string';
import { DateFromFormat, DateFromISOToBrazilianFormat, validateDateFromBrazilianForm } from 'src/helpers/Date';
import { removeSpacesAndSpecialChars } from 'src/helpers/removeSpacesAndSpecialChars';

import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';

import { PrescriberService } from 'src/services/prescriber';
import { DateTime } from 'luxon';
import { useAssistantContext } from 'src/providers/AssistantProvider';
import { AssistantService } from 'src/services/AssistantService';

interface PersonalDataFormFields {
  fullName: string;
  cpf: string;
  cellphoneNumber: string;
  email: string;
  birthdate: string;
}

const personalDataFormSchema = Yup.object().shape({
  fullName: Yup.string().required('Informe seu nome completo.'),
  cpf: Yup.string()
    .required('Informe um número de CPF válido.')
    .test('validate cpf', 'Informe um número de CPF válido.', (cpf) => {
      return validateCPF(cpf || '');
    }),
  cellphoneNumber: Yup.string()
    .required('Informe um número de celular válido.')
    .test('validate phoneNumber', 'Informe um número de celular válido.', (cellphoneNumber) => {
      if (!cellphoneNumber) return false;
      return onlyDigits(cellphoneNumber).length === 11;
    }),
  email: Yup.string().email('Informe um email válido.').required('Informe um email válido.'),
  birthdate: Yup.string().test('match', 'Informe uma data de nascimento válida.', (birthdate) => {
    return validateDateFromBrazilianForm(birthdate);
  }),
});

const initialValues: PersonalDataFormFields = {
  fullName: '',
  cpf: '',
  cellphoneNumber: '',
  email: '',
  birthdate: '',
};

interface PersonalDataFormProps {
  onEdit?(): void;
}

export function PersonalDataForm(props: PersonalDataFormProps) {
  const { assistantData, setAssistantData } = useAssistantContext();
  const { openToast } = useFeedbackContext();

  const { values, errors, isSubmitting, handleSubmit, setValues, validateField, handleChange } =
    useFormik<PersonalDataFormFields>({
      initialValues: initialValues,
      validationSchema: personalDataFormSchema,
      validateOnChange: false,
      onSubmit: onSubmit,
    });

  async function onSubmit(data: PersonalDataFormFields) {
    try {
      if (!assistantData) return;

      const date = DateFromFormat(values.birthdate || '', 'dd/MM/yyyy');

      await AssistantService.updateAssistant(assistantData._id, {
        ...assistantData,
        ...data,
        fullName: removeSpacesAndSpecialChars(data.fullName),
        birthdate: DateTime.fromISO(date).toString(),
      });

      setAssistantData({
        ...assistantData,
        ...data,
        fullName: removeSpacesAndSpecialChars(data.fullName),
        birthdate: date,
      });

      openToast('Dados salvos com sucesso.', {
        type: 'success',
        duration: 2000,
      });

      if (props.onEdit) {
        props.onEdit();
      }
    } catch (error: any) {
      if (error.response.status === 500) {
        openToast('Serviço se encontra fora do ar. Tente novamente mais tarde.', {
          type: 'error',
          duration: 6000,
        });

        return;
      }

      openToast('Ocorreu um erro ao salvar seus dados. Tente novamente em instantes.', {
        type: 'error',
        duration: 2000,
      });
    }
  }

  useEffect(() => {
    if (!assistantData) return;

    setValues({
      fullName: assistantData.fullName,
      cpf: assistantData.cpf,
      cellphoneNumber: assistantData.cellphoneNumber,
      email: assistantData.email,
      birthdate: assistantData.birthdate ? DateFromISOToBrazilianFormat(String(assistantData.birthdate)) : '',
    });
  }, [assistantData, setValues]);

  if (!assistantData) {
    return <PersonalDataFormSkeleton />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        height: '100%',
      }}
      data-testId={'personal-data-form'}
    >
      <Flex
        flexDirection={'column'}
        gap={'20px'}
        bgColor={{
          base: 'transparent',
          lg: 'white',
        }}
        borderRadius={'20px'}
        p={{ base: '0px', sm: '32px' }}
        h={'full'}
      >
        <Box display={{ base: 'none', sm: 'block' }}>
          <Typography model={'h2'} size={'large'} weight={'bold'}>
            Dados pessoais
          </Typography>
        </Box>

        <FormControl isInvalid={!!errors.fullName}>
          <MevoInput
            name={'fullName'}
            placeholder={'Digite seu nome'}
            labelText={'Nome Completo*'}
            value={values.fullName}
            onChange={handleChange}
            onBlur={() => validateField('fullName')}
            errorText={errors.fullName}
            m={0}
            autoFocus
          />
        </FormControl>

        <SimpleGrid
          columns={{
            base: 1,
            xl: 2,
          }}
          gap={'20px'}
        >
          <FormControl isInvalid={!!errors.cpf}>
            <MevoInput
              name={'cpf'}
              placeholder={'000.000.000-00'}
              labelText={'CPF*'}
              value={values.cpf}
              mask={MASKS.CPF}
              onChange={handleChange}
              onBlur={() => validateField('cpf')}
              errorText={errors.cpf}
              isDisabled={true}
              m={0}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.cellphoneNumber}>
            <MevoInput
              name={'cellphoneNumber'}
              placeholder={'(+55) 0 0000-0000'}
              labelText={'Celular*'}
              mask={MASKS.BRAZIL_PHONE}
              value={values.cellphoneNumber}
              onChange={handleChange}
              onBlur={() => validateField('cellphoneNumber')}
              errorText={errors.cellphoneNumber}
              m={0}
            />
          </FormControl>
        </SimpleGrid>

        <SimpleGrid
          columns={{
            base: 1,
            xl: 2,
          }}
          gap={'20px'}
        >
          <FormControl isInvalid={!!errors.email}>
            <MevoInput
              name={'email'}
              placeholder={'Digite seu email'}
              labelText={'Email*'}
              type={'email'}
              value={values.email}
              onChange={handleChange}
              onBlur={() => validateField('email')}
              errorText={errors.email}
              isDisabled={true}
              m={0}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.birthdate}>
            <MevoInput
              name={'birthdate'}
              placeholder={'Digite sua data de nascimento'}
              labelText={'Data de nascimento'}
              mask={MASKS.BIRTH_DATE}
              maskChar={''}
              value={values.birthdate}
              onChange={handleChange}
              onBlur={() => validateField('birthdate')}
              errorText={errors.birthdate}
              m={0}
            />
          </FormControl>
        </SimpleGrid>

        <Button
          type={'submit'}
          isLoading={isSubmitting}
          w={{
            base: 'full',
            md: '150px',
          }}
          h={'60px'}
          alignSelf={'flex-end'}
          mt={'auto'}
        >
          Salvar
        </Button>
      </Flex>
    </form>
  );
}
